import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl,
    FormLabel,
    makeStyles,
    Typography
} from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
    preview: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(2),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.background.default,
        minHeight: "100px"
    },
    imagePreview: {
        maxWidth: "100%",
        maxHeight: "200px",
        marginTop: theme.spacing(1)
    }
}));

const EmailSignatureModal = ({ open, onClose, smtpConfigId }) => {
    const classes = useStyles();
    const [signatureType, setSignatureType] = useState("text");
    const [signatureText, setSignatureText] = useState("");
    const [signatureImage, setSignatureImage] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open && smtpConfigId) {
            fetchSignature();
        }
    }, [open, smtpConfigId]);

    const fetchSignature = async () => {
        try {
            const { data } = await api.get(`/smtp-configs/${smtpConfigId}`);
            if (data.signature) {
                setSignatureType(data.signatureType || "text");
                if (data.signatureType === "image") {
                    setSignatureImage(data.signature);
                } else {
                    setSignatureText(data.signature);
                }
            }
        } catch (err) {
            toastError(err);
        }
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validar tamanho (máx 5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast.error("Imagem muito grande! Máximo 5MB");
            return;
        }

        const formData = new FormData();
        formData.append("file", file);

        setLoading(true);
        try {
            const { data } = await api.post("/signatures/upload", formData, {
                headers: {
                    "Content-Type": "multipart/form-data"
                }
            });
            setSignatureImage(data.url);
            toast.success("Imagem carregada com sucesso!");
        } catch (err) {
            toastError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!smtpConfigId) {
            toast.error("Selecione uma conta de e-mail");
            return;
        }

        const signature = signatureType === "image" ? signatureImage : signatureText;

        if (!signature) {
            toast.error("Preencha a assinatura");
            return;
        }

        setLoading(true);
        try {
            await api.put(`/smtp-configs/${smtpConfigId}`, {
                signature,
                signatureType
            });

            toast.success("Assinatura salva com sucesso!");
            onClose();
        } catch (err) {
            toastError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setSignatureText("");
        setSignatureImage("");
        setSignatureType("text");
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Configurar Assinatura de E-mail</DialogTitle>
            <DialogContent>
                <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend">Tipo de Assinatura</FormLabel>
                    <RadioGroup
                        value={signatureType}
                        onChange={(e) => setSignatureType(e.target.value)}
                    >
                        <FormControlLabel
                            value="text"
                            control={<Radio />}
                            label="Texto"
                        />
                        <FormControlLabel
                            value="image"
                            control={<Radio />}
                            label="Imagem"
                        />
                    </RadioGroup>
                </FormControl>

                {signatureType === "text" ? (
                    <>
                        <TextField
                            label="Assinatura (Texto)"
                            multiline
                            rows={6}
                            fullWidth
                            variant="outlined"
                            value={signatureText}
                            onChange={(e) => setSignatureText(e.target.value)}
                            placeholder="Ex:&#10;Atenciosamente,&#10;Seu Nome&#10;Empresa&#10;Telefone"
                            margin="normal"
                        />
                        {signatureText && (
                            <div className={classes.preview}>
                                <Typography variant="caption" color="textSecondary">
                                    Preview:
                                </Typography>
                                <div style={{ whiteSpace: "pre-wrap", marginTop: 8 }}>
                                    {signatureText}
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        <input
                            accept="image/*"
                            style={{ display: "none" }}
                            id="signature-image-upload"
                            type="file"
                            onChange={handleImageUpload}
                        />
                        <label htmlFor="signature-image-upload">
                            <Button
                                variant="outlined"
                                component="span"
                                fullWidth
                                style={{ marginTop: 16 }}
                            >
                                {signatureImage ? "Trocar Imagem" : "Selecionar Imagem"}
                            </Button>
                        </label>
                        {signatureImage && (
                            <div className={classes.preview}>
                                <Typography variant="caption" color="textSecondary">
                                    Preview:
                                </Typography>
                                <img
                                    src={signatureImage}
                                    alt="Assinatura"
                                    className={classes.imagePreview}
                                />
                            </div>
                        )}
                        <Typography variant="caption" color="textSecondary" style={{ marginTop: 8, display: "block" }}>
                            Tamanho máximo: 500KB
                        </Typography>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancelar
                </Button>
                <Button
                    onClick={handleSave}
                    color="primary"
                    variant="contained"
                    disabled={loading}
                >
                    {loading ? "Salvando..." : "Salvar"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EmailSignatureModal;
